import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Divider,
  Box,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

const toCamelCase = (str) => {
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

const OneTermMarksheetReceiptBulk = ({ receiptData, school, setTemID }) => {
  // Each entry here is a fully self-contained, single-term marksheet for
  // one student — so this array can safely hold many students already.
  const students = receiptData.data || [];

  const receiptId = `marksheet`;

  // --- Customization controls (theme color + term filter) ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all"); // "all" | one of examType values present in data

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Discover whatever exam types actually exist in the data (e.g. "First",
  // "Second", "Final", or custom names), so the filter isn't hardcoded to
  // a fixed 3-term structure.
  const availableTerms = useMemo(() => {
    const set = new Set(
      students.map((s) => s.examType?.trim()).filter(Boolean)
    );
    return Array.from(set);
  }, [students]);

  const filteredStudents = useMemo(() => {
    if (selectedTerm === "all") return students;
    return students.filter(
      (s) => s.examType?.trim().toLowerCase() === selectedTerm.trim().toLowerCase()
    );
  }, [students, selectedTerm]);

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);
  const thStyle = { border: `1px solid ${borderColor}`, padding: "8px", background: headerBg };
  const tdStyle = { border: `1px solid ${borderColor}`, padding: "8px" };

  return (
    <Box id={receiptId}>
      {/* Controls: hidden when printing */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "800px",
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Theme Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 40,
              height: 32,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="term-filter-label">Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {availableTerms.map((t) => (
              <MenuItem key={t} value={t}>
                {toCamelCase(t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredStudents.map((ms, idx) => {
        const marks = ms.marks || [];
        const totalMax = marks.reduce((sum, m) => sum + Number(m.maxMark || 0), 0);

        return (
          // key must live on the top-level element returned per iteration
          <React.Fragment key={ms.id ?? idx}>
            <Box
              sx={{
                maxWidth: "800px",
                margin: "auto",
                px: 4,
                pb: 4,
                border: `2px solid ${borderColor}`,
                borderRadius: "12px",
                backgroundColor: "#fff",
                backgroundImage: `url(${school?.logoUrl?.replace(/\\/g, "/")})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "400px auto",
                backgroundBlendMode: "lighten",
              }}
            >
              {/* Header with Logo */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                flexDirection={"column"}
              >
                <img
                  src={school.logoUrl}
                  alt="School Logo"
                  style={{ width: 90, height: 80, marginTop: 10 }}
                />
                <Typography variant="h4" sx={{ fontWeight: "bold", color: borderColor }}>
                  {toCamelCase(school.schoolName)}
                </Typography>
                <Typography sx={{ pb: 1 }}>
                  {toCamelCase(
                    [
                      school?.addressDTO?.addressLine1,
                      school?.addressDTO?.city,
                      school?.addressDTO?.pincode,
                      school?.addressDTO?.state,
                    ].join(", ")
                  )}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    bgcolor: headerBg,
                    borderRadius: 8,
                    fontWeight: "bold",
                    px: 2,
                    py: 0.2,
                    borderBottom: `3px solid ${borderColor}`,
                    borderLeft: `2px solid ${borderColor}`,
                    borderRight: `1px solid ${borderColor}`,
                    mb: 2,
                  }}
                >
                  {"MARKS STATEMENT"}
                </Typography>
              </Box>

              <Box spacing={2} display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  Roll No: <b>{ms.rollNumber || ms.id}</b>
                </Typography>
                <Typography>
                  {toCamelCase(`${ms.examType}`)} {ms.academicYear}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2, mt: 1, borderColor }} />

              {/* Student Info */}
              <Grid container spacing={2} display="flex" justifyContent="space-between">
                <Typography>
                  <b>Student Name:</b> {toCamelCase(ms.studentName)}
                </Typography>
                <Typography>
                  <b>Standard:</b> {ms?.classRoom?.standard} | {ms?.classRoom?.section}
                </Typography>
              </Grid>
              <Divider sx={{ mb: 2, mt: 1, borderColor }} />

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Subject</th>
                    <th style={thStyle}>Marks Obtained</th>
                    <th style={thStyle}>Max Marks</th>
                    <th style={thStyle}>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {marks.map((m, index) => (
                    <tr key={index}>
                      <td style={{ ...tdStyle, textAlign: "center" }}>{index + 1}</td>
                      <td style={tdStyle}>{toCamelCase(`${m.subjectName}`)}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{m.marksObtained}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{m.maxMark}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{m.grade}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: totalsBg }}>
                    <td colSpan="2" style={{ ...tdStyle, textAlign: "right" }}>
                      <b>Result: </b> {ms.result ? ms.result : "Fail"}
                    </td>
                    <td colSpan="1" style={{ ...tdStyle, textAlign: "right" }}>
                      <b>{totalMax}</b>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <b>{ms.totalMarks}</b>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <b>{ms.grade}</b>
                    </td>
                  </tr>
                </tfoot>
              </table>

              <Box variant="body1" sx={{ my: 2 }} display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  <strong>Total Marks:</strong> {ms.totalMarks}/{totalMax}
                </Typography>
                <Typography variant="body2">
                  <strong>Percentage:</strong>{" "}
                  {typeof ms.percentage === "number" ? ms.percentage.toFixed(2) : ms.percentage}%{" "}
                </Typography>
                <Typography variant="body2">
                  <strong>Grade:</strong> {ms.grade}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                <b>Remarks: </b>
                {ms.remarks}
              </Typography>
              <Box my={8} display="flex" justifyContent="space-between">
                <Typography variant="body2">Signature: ___________________</Typography>
                <Typography variant="body2">
                  Class Teacher Sign.: ___________________
                </Typography>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {" "}
                    Principal/HeadMaster{" "}
                  </Typography>
                  <Typography variant="body2">Seal/Sign.: ___________________</Typography>
                </Box>
              </Box>
            </Box>

            {/* page break between students (not after the very last one) */}
            {idx !== filteredStudents.length - 1 && <div className="pagebreak"></div>}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default OneTermMarksheetReceiptBulk;
